export enum ChatSessionStatus {
  /** With the AI only; joins the agent queue (WAITING) on a handoff. */
  BOT = 'bot',
  WAITING = 'waiting',
  ACTIVE = 'active',
  ENDED = 'ended',
}

export enum MessageType {
  TEXT = 'text',
  IMAGE = 'image',
  FILE = 'file',
  SYSTEM = 'system',
}

export enum SenderType {
  VISITOR = 'visitor',
  CUSTOMER = 'customer',
  AGENT = 'agent',
  SYSTEM = 'system',
  BOT = 'bot',
}

export interface ChatMessage {
  id: string;
  sessionId: string;
  content: string;
  type: MessageType;
  senderType: SenderType;
  senderId?: string;
  senderName?: string;
  metadata?: Record<string, any>;
  createdAt: string;
  readAt?: string;
}

export interface ChatSession {
  id: string;
  tenantId: string;
  visitorId?: string;
  customerId?: string;
  /** Who holds the chat (the server's assignedAgentId, see chatService.withAgent). */
  agentId?: string;
  assignedAgentId?: string;
  status: ChatSessionStatus;
  channel: string;
  ticketId?: string;
  conversationId?: string;
  metadata?: {
    visitorName?: string;
    visitorEmail?: string;
    browserInfo?: string;
    pageUrl?: string;
    // Handoff info from AI agent
    conversationSummary?: string;
    handoffReason?: string;
    handoffTriggeredAt?: string;
    [key: string]: any;
  };
  startedAt: string;
  endedAt?: string;
  rating?: number;
  feedback?: string;
  messageCount?: number;
  lastMessageAt?: string;
  lastMessage?: ChatMessage;
}

export interface ChatStats {
  waiting: number;
  active: number;
  endedToday: number;
  /** Chats the assistant is holding. */
  bot?: number;
  longestWaitSeconds?: number | null;
  /** The signed-in agent's own figures today. */
  mine?: { active: number; endedToday: number; avgHandleSeconds: number | null };
  /** Admin only: team-wide times today and the per-agent breakdown. */
  avgWaitToAcceptSeconds?: number | null;
  avgHandleSeconds?: number | null;
  agents?: Array<{
    agentId: string;
    active: number;
    endedToday: number;
    avgHandleSeconds: number | null;
    online: boolean;
  }>;
}

export interface PaginatedChatSessions {
  data: ChatSession[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface SendMessageDto {
  content: string;
  type?: MessageType;
  senderType: SenderType;
  senderId?: string;
  metadata?: Record<string, any>;
}

export interface AssignAgentDto {
  agentId: string;
}

export interface TransferChatDto {
  targetAgentId: string;
  reason?: string;
}

export interface EndChatSessionDto {
  reason?: string;
  rating?: number;
  feedback?: string;
  createTicket?: boolean;
  ticketSubject?: string;
  ticketStatus?: 'new' | 'resolved';
  assigneeId?: string;
}

export interface LinkToTicketDto {
  ticketId: string;
  conversationId: string;
}
